import React, { useState } from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap'
import Select from 'react-select'

import PropTypes from 'prop-types'

import MagicWand from './MagicWand'

const STATE_OPTIONS = [
  // 50 States
  { value: 'AL', label: 'AL - Alabama' },
  { value: 'AK', label: 'AK - Alaska' },
  { value: 'AZ', label: 'AZ - Arizona' },
  { value: 'AR', label: 'AR - Arkansas' },
  { value: 'CA', label: 'CA - California' },
  { value: 'CO', label: 'CO - Colorado' },
  { value: 'CT', label: 'CT - Connecticut' },
  { value: 'DE', label: 'DE - Delaware' },
  { value: 'FL', label: 'FL - Florida' },
  { value: 'GA', label: 'GA - Georgia' },
  { value: 'HI', label: 'HI - Hawaii' },
  { value: 'ID', label: 'ID - Idaho' },
  { value: 'IL', label: 'IL - Illinois' },
  { value: 'IN', label: 'IN - Indiana' },
  { value: 'IA', label: 'IA - Iowa' },
  { value: 'KS', label: 'KS - Kansas' },
  { value: 'KY', label: 'KY - Kentucky' },
  { value: 'LA', label: 'LA - Louisiana' },
  { value: 'ME', label: 'ME - Maine' },
  { value: 'MD', label: 'MD - Maryland' },
  { value: 'MA', label: 'MA - Massachusetts' },
  { value: 'MI', label: 'MI - Michigan' },
  { value: 'MN', label: 'MN - Minnesota' },
  { value: 'MS', label: 'MS - Mississippi' },
  { value: 'MO', label: 'MO - Missouri' },
  { value: 'MT', label: 'MT - Montana' },
  { value: 'NE', label: 'NE - Nebraska' },
  { value: 'NV', label: 'NV - Nevada' },
  { value: 'NH', label: 'NH - New Hampshire' },
  { value: 'NJ', label: 'NJ - New Jersey' },
  { value: 'NM', label: 'NM - New Mexico' },
  { value: 'NY', label: 'NY - New York' },
  { value: 'NC', label: 'NC - North Carolina' },
  { value: 'ND', label: 'ND - North Dakota' },
  { value: 'OH', label: 'OH - Ohio' },
  { value: 'OK', label: 'OK - Oklahoma' },
  { value: 'OR', label: 'OR - Oregon' },
  { value: 'PA', label: 'PA - Pennsylvania' },
  { value: 'RI', label: 'RI - Rhode Island' },
  { value: 'SC', label: 'SC - South Carolina' },
  { value: 'SD', label: 'SD - South Dakota' },
  { value: 'TN', label: 'TN - Tennessee' },
  { value: 'TX', label: 'TX - Texas' },
  { value: 'UT', label: 'UT - Utah' },
  { value: 'VT', label: 'VT - Vermont' },
  { value: 'VA', label: 'VA - Virginia' },
  { value: 'WA', label: 'WA - Washington' },
  { value: 'WV', label: 'WV - West Virginia' },
  { value: 'WI', label: 'WI - Wisconsin' },
  { value: 'WY', label: 'WY - Wyoming' },

  // District of Columbia
  { value: 'DC', label: 'DC - District of Columbia' },

  // US Territories & Possessions
  { value: 'AS', label: 'AS - American Samoa' },
  { value: 'GU', label: 'GU - Guam' },
  { value: 'MP', label: 'MP - Northern Mariana Islands' },
  { value: 'PR', label: 'PR - Puerto Rico' },
  { value: 'VI', label: 'VI - Virgin Islands' },
  { value: 'FM', label: 'FM - Federated States of Micronesia' },
  { value: 'MH', label: 'MH - Marshall Islands' },
  { value: 'PW', label: 'PW - Palau' },

  // Military
  { value: 'AA', label: 'AA - Armed Forces Americas' },
  { value: 'AE', label: 'AE - Armed Forces Europe' },
  { value: 'AP', label: 'AP - Armed Forces Pacific' },
]

const STATE_TYPO_MAP = {
  masachusetts: 'MA',
  massachusets: 'MA',
  massachussetts: 'MA',
  conneticut: 'CT',
  pensylvania: 'PA',
  pennsilvania: 'PA',
  misisipi: 'MS',
  missisipi: 'MS',
  mississipi: 'MS',
  louisianna: 'LA',
  hawai: 'HI',
  tenesee: 'TN',
  tennesee: 'TN',
}

const STATE_MAP = {}
STATE_OPTIONS.forEach(opt => {
  STATE_MAP[opt.value.toLowerCase()] = opt.value
  const fullName = opt.label.substring(5).toLowerCase()
  STATE_MAP[fullName] = opt.value
})
Object.keys(STATE_TYPO_MAP).forEach(typo => {
  STATE_MAP[typo] = STATE_TYPO_MAP[typo]
})

const stateNamesPattern = Object.keys(STATE_MAP)
  .sort((a, b) => b.length - a.length)
  // eslint-disable-next-line no-useless-escape
  .map(name => name.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'))
  .join('|')
const STATE_REGEX = new RegExp(`\\b(${stateNamesPattern})\\s*$`, 'i')

const normalizeState = val => {
  if (!val) return ''
  const trimmed = val.trim().toLowerCase()
  if (STATE_MAP[trimmed]) {
    return STATE_MAP[trimmed]
  }
  return val.length === 2 ? val.toUpperCase() : val
}

const SmartAddressInput = ({ value, onChange, error, disabled }) => {
  const [isApplying, setIsApplying] = useState(false)
  const [toast, setToast] = useState(null)

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  // Destructure incoming value or default to empty
  const street = value?.street ?? ''
  const city = value?.city ?? ''
  const state = normalizeState(value?.state ?? '')
  const zip = value?.zip ?? ''

  const handleManualChange = (field, newVal) => {
    onChange({
      ...value,
      [field]: newVal,
    })
  }

  const parseAddress = rawAddress => {
    // 1. Split into lines to check for multi-line inputs
    const lines = rawAddress
      .split('\n')
      .map(line => line.trim())
      .filter(line => line)

    if (lines.length === 0) {
      return { street: '', city: '', state: '', zip: '' }
    }

    // 2. Identify the "City, State Zip" line (usually the last line)
    // We look for the last line that matches the pattern.
    let cityStateZipLineIndex = -1
    let zipCode = ''
    let stateCode = ''
    let cityStr = ''

    // Iterate backwards to find the likely City/State/Zip line
    for (let i = lines.length - 1; i >= 0; i--) {
      const line = lines[i]
      // Match Zip (5 or 9 digits at end)
      let remaining = line

      // Match Zip (5 or 9 digits at end)
      const zipMatch = remaining.match(/(\d{5}(?:-\d{4})?)\s*$/)
      if (zipMatch) {
        zipCode = zipMatch[1]
        remaining = remaining.replace(zipMatch[0], '').trim().replace(/,\s*$/, '')
      }

      // Match State (abbreviations, full names, or common typos at end)
      const stateMatch = remaining.match(STATE_REGEX)
      if (stateMatch) {
        const candidateState = stateMatch[1]
        const textBeforeState = remaining.substring(0, remaining.length - stateMatch[0].length)

        // If we found a zip, we trust the state match more.
        // If no zip, we require a comma immediately preceding the state (ignoring space)
        const hasCommaBefore = /,\s*$/.test(textBeforeState)

        if (zipCode || hasCommaBefore) {
          stateCode = normalizeState(candidateState)
          remaining = remaining.replace(stateMatch[0], '').trim().replace(/,\s*$/, '')

          // Remaining is "City" OR "Street, City"
          const lastCommaIndex = remaining.lastIndexOf(',')
          if (lastCommaIndex > -1) {
            cityStr = remaining.substring(lastCommaIndex + 1).trim()
            const extraStreetPart = remaining.substring(0, lastCommaIndex).trim()
            if (extraStreetPart) {
              lines[i] = extraStreetPart
            } else {
              lines[i] = ''
            }
          } else {
            // No comma, so the whole remaining part is likely the City
            cityStr = remaining
            lines[i] = ''
          }

          cityStateZipLineIndex = i
          break
        }
      }
    }

    // 3. Construct Street
    // Everything BEFORE the cityStateZipLineIndex (or including it if we modified it above)
    // Actually, if we scraped City/State/Zip out of lines[i], we left the "Street Part" in lines[i] or empty string.
    // So we just take all lines up to cityStateZipLineIndex (inclusive) that are not empty.

    // If NO match found, treat everything as street? Or try fallback?
    // Current logic: if index > -1, we consume lines.

    let streetLines = []
    if (cityStateZipLineIndex > -1) {
      for (let k = 0; k <= cityStateZipLineIndex; k++) {
        if (lines[k]) streetLines.push(lines[k])
      }
    } else {
      // Fallback: If no State/Zip pattern found, treats whole thing as street?
      // Or maybe just try to parse single line assuming format "Street, City, State Zip" without strict newlines?
      // For now, if no match, return entire input as street
      streetLines = lines
    }

    // 4. Format Street: Replace commas with newlines
    // "30 S. Nevada Ave., STE LO1, Mail Code 015" ->
    // 30 S. Nevada Ave.
    // STE LO1
    // Mail Code 015
    let finalStreet = streetLines.join('\n')
    // Split by comma and rejoin with newline, but respect existing newlines?
    // Actually best to join with comma first then split everything by comma?
    // User wants: "Source has commas" -> "Target has newlines"
    // So: "Part A, Part B" -> "Part A\nPart B"

    // We replace all commas with newlines, then clean up extra whitespace
    finalStreet = finalStreet
      .split(',')
      .map(s => s.trim())
      .filter(s => s)
      .join('\n')

    return {
      street: finalStreet,
      city: cityStr,
      state: stateCode,
      zip: zipCode,
    }
  }

  const handleSmartPasteClick = async () => {
    if (disabled) return
    let text
    try {
      text = await navigator.clipboard.readText()
    } catch {
      showToast('Could not read clipboard.', 'error')
      return
    }
    if (!text.trim()) {
      showToast('Clipboard has no text. Copy an address first.', 'warning')
      return
    }
    setIsApplying(true)
    const parsed = parseAddress(text)
    onChange({ ...value, ...parsed })
    const filled = ['Street', 'City', 'State', 'Zip'].filter(f => parsed[f.toLowerCase()])
    showToast(
      filled.length > 0 ? `Pasted: ${filled.join(', ')}` : 'No address recognized.',
      filled.length > 0 ? 'success' : 'warning',
    )
    setIsApplying(false)
  }

  return (
    <div className="smart-address-input">
      {/* Smart Paste Header */}
      <div className="d-flex justify-content-end align-items-center mb-2">
        <Button
          variant="link"
          className="p-0 text-muted small"
          onClick={handleSmartPasteClick}
          disabled={isApplying || disabled}
        >
          <MagicWand active={isApplying} className="mr-1" />
          Smart Paste Address
        </Button>
        {toast && (
          <small
            className={`ml-2 ${
              toast.type === 'success' ? 'text-success' : toast.type === 'error' ? 'text-danger' : 'text-warning'
            }`}
          >
            {toast.message}
          </small>
        )}
      </div>

      {/* Manual Fields */}
      <Form.Group controlId="addressStreet">
        <Form.Label>Street</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={street}
          onChange={e => handleManualChange('street', e.target.value)}
          placeholder="Street Address, P.O. Box, Company Name, c/o"
          disabled={disabled}
          isInvalid={!!error?.street}
        />
        <Form.Control.Feedback type="invalid">{error?.street}</Form.Control.Feedback>
      </Form.Group>

      <Row>
        <Col md={5}>
          <Form.Group controlId="addressCity">
            <Form.Label>City</Form.Label>
            <Form.Control
              type="text"
              value={city}
              onChange={e => handleManualChange('city', e.target.value)}
              disabled={disabled}
              isInvalid={!!error?.city}
            />
            <Form.Control.Feedback type="invalid">{error?.city}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="addressState">
            <Form.Label>State</Form.Label>
            <Select
              className={error?.state ? 'is-invalid' : ''}
              classNamePrefix="react-select"
              options={STATE_OPTIONS}
              value={STATE_OPTIONS.find(opt => opt.value === normalizeState(state)) || null}
              onChange={opt => handleManualChange('state', opt ? opt.value : '')}
              isDisabled={disabled}
              placeholder="Select..."
              isClearable
              styles={{
                control: (provided, stateControl) => ({
                  ...provided,
                  borderColor: error?.state ? '#dc3545' : provided.borderColor,
                  boxShadow:
                    stateControl.isFocused && !!error?.state
                      ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)'
                      : provided.boxShadow,
                  minHeight: '38px',
                  height: '38px',
                }),
                valueContainer: provided => ({
                  ...provided,
                  height: '38px',
                  padding: '0 6px',
                }),
                input: provided => ({
                  ...provided,
                  margin: '0px',
                }),
                indicatorsContainer: provided => ({
                  ...provided,
                  height: '38px',
                }),
              }}
            />
            {!!error?.state && <div className="invalid-feedback d-block">{error?.state}</div>}
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="addressZip">
            <Form.Label>Zip</Form.Label>
            <Form.Control
              type="text"
              value={zip}
              onChange={e => handleManualChange('zip', e.target.value)}
              disabled={disabled}
              isInvalid={!!error?.zip}
            />
            <Form.Control.Feedback type="invalid">{error?.zip}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
    </div>
  )
}

SmartAddressInput.propTypes = {
  value: PropTypes.shape({
    street: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.object,
  disabled: PropTypes.bool,
}

export default SmartAddressInput
